import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import Common from '../../../shared/Common';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DictionaryContext } from '../../../../App';
import debounce from 'lodash.debounce';
import { IGenericFinancialValue } from './types';

interface IPredictionStatisticsProps {
  chartHeight: number;
  showPredictions: boolean;
  monthlyFinancialData: IGenericFinancialValue[] | undefined;
}

const PredictionStatistics = (props: IPredictionStatisticsProps) => {
  const [predictionStartDate, setPredictionStartDate] = useState<Date>(
    new Date(new Date().setMonth(new Date().getMonth() - 6))
  );
  const [predictionEndDate, setPredictionEndDate] = useState<Date>(
    new Date(new Date().setMonth(new Date().getMonth() + 6))
  );
  const [dataCalculationDate, setDataCalculationDate] = useState<Date>(Common.Utils.getPreviousMonthDate(new Date()));
  const [predictionStartingAmount, setPredictionStartingAmount] = useState<number>(0);
  const [predictionTargetBalance, setPredictionTargetBalance] = useState<number>(0);
  const [interestRate, setInterestRate] = useState<number>(0);
  const [interestCapitalization, setInterestCapitalization] = useState<1 | 3 | 12>(12);
  const [showAveragePredictionTime, setShowAveragePredictionTime] = useState<boolean>(true);
  const [showAverage3Months, setShowAverage3Months] = useState<boolean>(false);
  const [showAverage6Months, setShowAverage6Months] = useState<boolean>(false);
  const [showAverage12Months, setShowAverage12Months] = useState<boolean>(false);
  const [isSimulating, setIsSimulating] = useState<boolean>(false);
  const [simulationIntervalId, setSimulationIntervalId] = useState<ReturnType<typeof setInterval> | null>(null);

  const { userPreferences } = useContext(DictionaryContext);

  const debouncedInterestRateChanged = useMemo(
    () =>
      debounce((e: any) => {
        const value = Number(e.target.value);
        if (!isNaN(value)) {
          setInterestRate(value);
        }
      }, 500),
    []
  );
  const debouncedPredictionTargetBalance = useMemo(
    () =>
      debounce((e: any) => {
        const value = Number(e.target.value);
        if (!isNaN(value)) {
          setPredictionTargetBalance(value);
        }
      }, 500),
    []
  );
  const debouncedPredictionStartingAmount = useMemo(
    () =>
      debounce((e: any) => {
        const value = Number(e.target.value);
        if (!isNaN(value)) {
          setPredictionStartingAmount(value);
        }
      }, 500),
    []
  );

  useEffect(
    () => () => {
      debouncedInterestRateChanged.cancel();
      debouncedPredictionTargetBalance.cancel();
      debouncedPredictionStartingAmount.cancel();
      if (simulationIntervalId) {
        clearInterval(simulationIntervalId);
      }
    },
    [
      debouncedInterestRateChanged,
      debouncedPredictionTargetBalance,
      debouncedPredictionStartingAmount,
      simulationIntervalId,
    ]
  );

  useEffect(() => {
    if (userPreferences?.predictionStartingAmount) {
      setPredictionStartingAmount(userPreferences?.predictionStartingAmount);
    }
    if (userPreferences?.predictionTargetBalance) {
      setPredictionTargetBalance(userPreferences?.predictionTargetBalance);
    }
    if (userPreferences?.predictionStartDate) {
      setPredictionStartDate(new Date(userPreferences?.predictionStartDate));
    }
    if (userPreferences?.predictionEndDate) {
      setPredictionEndDate(new Date(userPreferences?.predictionEndDate));
    }
    if (userPreferences?.interestRate) {
      setInterestRate(userPreferences?.interestRate);
    }
    if (userPreferences?.interestCapitalization) {
      setInterestCapitalization(userPreferences?.interestCapitalization);
    }
    if (userPreferences?.showAveragePredictionTime) {
      setShowAveragePredictionTime(userPreferences?.showAveragePredictionTime);
    }
    if (userPreferences?.showAverage3Months) {
      setShowAverage3Months(userPreferences?.showAverage3Months);
    }
    if (userPreferences?.showAverage6Months) {
      setShowAverage6Months(userPreferences?.showAverage6Months);
    }
    if (userPreferences?.showAverage12Months) {
      setShowAverage12Months(userPreferences?.showAverage12Months);
    }
  }, [userPreferences]);

  const calculateBalanceAverageForLastMonths = (months: number, chartData: any[]) =>
    chartData.length >= months
      ? chartData.slice(-months).reduce((acc, val) => acc + val.balance, 0) / months
      : chartData.length > 0
        ? chartData.reduce((acc, val) => acc + val.balance, 0) / chartData.length
        : 0;

  const handleSimulation = useCallback(() => {
    if (isSimulating && simulationIntervalId) {
      clearInterval(simulationIntervalId);
      setSimulationIntervalId(null);
      setIsSimulating(false);
      return;
    }

    setIsSimulating(true);
    const startDate = new Date(predictionStartDate);
    const today = new Date();

    const monthsDifference =
      (today.getFullYear() - startDate.getFullYear()) * 12 + (today.getMonth() - startDate.getMonth());
    let intervalDuration = 1000;
    if (intervalDuration * monthsDifference > 30000) {
      intervalDuration = 30000 / monthsDifference;
    }
    // Ustaw początkową datę symulacji
    setDataCalculationDate(new Date(startDate));

    let simulationDate = new Date(startDate);

    const interval = setInterval(() => {
      simulationDate = Common.Utils.addMonths(simulationDate, 1);

      if (simulationDate >= today) {
        clearInterval(interval);
        setSimulationIntervalId(null);
        setIsSimulating(false);
        setDataCalculationDate(new Date(today));
        return;
      }

      setDataCalculationDate(new Date(simulationDate));
    }, intervalDuration);

    setSimulationIntervalId(interval);
  }, [isSimulating, simulationIntervalId, predictionStartDate]);

  const generatePredictionsData = useCallback(
    (financialData: IGenericFinancialValue[] | undefined) => {
      const chartColors = {
        cumulativeBalance: '#3634b1ff',
        cumulativeBalanceArea: 'rgba(41, 128, 185, 0.15)',
        cumulativeBalanceByPredictionTimeAvg: '#3634b1ff',
        cumulativeBalanceByLast3MonthAvg: 'rgb(44, 160, 101)',
        cumulativeBalanceByLast6MonthAvg: 'rgb(41, 128, 185)',
        cumulativeBalanceByLast12MonthAvg: 'rgb(214, 39, 40)',
        targetColor: 'rgb(255, 127, 14)',
        predictionReferenceColor: 'rgb(41, 128, 185)',
      };

      const RenderLegend = () => (
        <Container>
          <Row className='justify-content-center align-items-start' style={{ gap: '40px', paddingBottom: '5px' }}>
            <Col xs='auto'>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Form.Check
                  type='checkbox'
                  label='Średnia dotychczasowa'
                  id='show-average-all-time-checkbox'
                  checked={showAveragePredictionTime}
                  onChange={() => setShowAveragePredictionTime((prev) => !prev)}
                  style={{ fontSize: '12px', userSelect: 'none' }}
                />
                <Form.Check
                  type='checkbox'
                  label='Średnia z 3 miesięcy'
                  id='show-average-3-months-checkbox'
                  checked={showAverage3Months}
                  onChange={() => setShowAverage3Months((prev) => !prev)}
                  style={{ fontSize: '12px', userSelect: 'none' }}
                />
                <Form.Check
                  type='checkbox'
                  label='Średnia z 6 miesięcy'
                  id='show-average-6-months-checkbox'
                  checked={showAverage6Months}
                  onChange={() => setShowAverage6Months((prev) => !prev)}
                  style={{ fontSize: '12px', userSelect: 'none' }}
                />
                <Form.Check
                  type='checkbox'
                  label='Średnia z 12 miesięcy'
                  id='show-average-12-months-checkbox'
                  checked={showAverage12Months}
                  onChange={() => setShowAverage12Months((prev) => !prev)}
                  style={{ fontSize: '12px', userSelect: 'none' }}
                />
              </div>
            </Col>
            <Col xs='auto'>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Początek</label>
                  <Form.Control
                    type='month'
                    value={Common.Utils.getMonthYearString(predictionStartDate)}
                    max={Common.Utils.getMonthYearString(new Date())}
                    onChange={(e) => {
                      const predictionDate = new Date(e.target.value);
                      if (predictionDate > dataCalculationDate) {
                        setDataCalculationDate(predictionDate);
                      }
                      setPredictionStartDate(predictionDate);
                    }}
                    style={{
                      width: '140px',
                      height: '25px',
                      fontSize: '12px',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Koniec</label>
                  <Form.Control
                    type='month'
                    value={Common.Utils.getMonthYearString(predictionEndDate)}
                    min={Common.Utils.getMonthYearString(predictionStartDate)}
                    onChange={(e) => {
                      const predictionDate = new Date(e.target.value);
                      if (predictionDate < dataCalculationDate) {
                        setDataCalculationDate(predictionDate);
                      }
                      setPredictionEndDate(predictionDate);
                    }}
                    style={{
                      width: '140px',
                      height: '25px',
                      fontSize: '12px',
                    }}
                  />
                </div>
              </div>
            </Col>
            <Col xs='auto'>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Start (zł)</label>
                  <Form.Control
                    type='text'
                    defaultValue={predictionStartingAmount}
                    onChange={debouncedPredictionStartingAmount}
                    style={{
                      width: '100px',
                      height: '25px',
                      fontSize: '12px',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Cel (zł)</label>
                  <Form.Control
                    type='text'
                    defaultValue={predictionTargetBalance}
                    onChange={debouncedPredictionTargetBalance}
                    style={{
                      width: '100px',
                      height: '25px',
                      fontSize: '12px',
                    }}
                  />
                </div>
              </div>
            </Col>
            <Col xs='auto'>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Stopa procentowa (%)</label>
                  <Form.Control
                    type='text'
                    defaultValue={interestRate}
                    onChange={debouncedInterestRateChanged}
                    style={{
                      width: '100px',
                      height: '25px',
                      fontSize: '12px',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Kapitalizacja</label>
                  <Form.Select
                    value={interestCapitalization}
                    onChange={(e) => setInterestCapitalization(Number(e.target.value) as 1 | 3 | 12)}
                    style={{
                      width: '100px',
                      height: '25px',
                      fontSize: '12px',
                      padding: '2px 4px',
                    }}
                  >
                    <option value={1}>Miesięczna</option>
                    <option value={3}>Kwartałowa</option>
                    <option value={12}>Roczna</option>
                  </Form.Select>
                </div>
              </div>
            </Col>
            <Col xs='auto'>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Czas symulacji</label>
                  <Form.Control
                    type='month'
                    value={Common.Utils.getMonthYearString(dataCalculationDate)}
                    min={Common.Utils.getMonthYearString(predictionStartDate)}
                    max={Common.Utils.getMonthYearString(new Date())}
                    onChange={(e) => {
                      setDataCalculationDate(new Date(e.target.value));
                    }}
                    style={{
                      width: '140px',
                      height: '25px',
                      fontSize: '12px',
                    }}
                  />
                </div>
                <Button size='sm' variant='success' onClick={handleSimulation} disabled={false}>
                  {isSimulating ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                        aria-hidden='true'
                        style={{ width: '14px', height: '14px', borderWidth: '2px' }}
                      />
                      Symulacja
                    </>
                  ) : (
                    'Symulacja'
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      );
      const generateChartData = (data: IGenericFinancialValue[]) => {
        const currentDate = dataCalculationDate;
        const startDate = Common.Utils.getPreviousMonthDate(predictionStartDate);
        let chartData = data.map((item) => ({
          ...item,
          balance: Number((item.totalIncome - item.totalExpense).toFixed(2)),
        }));

        chartData = chartData.filter((e) => currentDate >= new Date(e.year, e.month - 1));

        const avgMonthlyBalanceByLast3Months = calculateBalanceAverageForLastMonths(3, chartData);
        const avgMonthlyBalanceByLast6Months = calculateBalanceAverageForLastMonths(6, chartData);
        const avgMonthlyBalanceByLast12Months = calculateBalanceAverageForLastMonths(12, chartData);

        chartData = chartData.filter((e) => new Date(e.year, e.month - 1) >= startDate);

        const avgMonthlyBalanceByPredictionTime =
          chartData.length > 0 ? chartData.reduce((acc, val) => acc + val.balance, 0) / chartData.length : 0;

        let cumulativeBalance = 0;
        const chartDataWithCumulative = chartData.map((item) => ({
          ...item,
          cumulativeBalance: (cumulativeBalance += item.balance),
          yearMonth: new Date(item.year, item.month - 1).getTime(),
          isPrediction: false,
        }));

        // Dodaj pola do ostatniego elementu
        if (chartDataWithCumulative.length > 0) {
          const lastItem = chartDataWithCumulative[chartDataWithCumulative.length - 1] as any;
          lastItem.cumulativeBalanceByPredictionTimeAvg = lastItem.cumulativeBalance;
          lastItem.cumulativeBalanceByLast3MonthAvg = lastItem.cumulativeBalance;
          lastItem.cumulativeBalanceByLast6MonthAvg = lastItem.cumulativeBalance;
          lastItem.cumulativeBalanceByLast12MonthAvg = lastItem.cumulativeBalance;
        }

        const predictions: any[] = [];
        let currentCumulativeBalanceByPredictionTimeAvg =
          chartDataWithCumulative[chartDataWithCumulative.length - 1]?.cumulativeBalance ?? 0;
        let currentCumulativeBalanceByLast3MonthAvg = currentCumulativeBalanceByPredictionTimeAvg;
        let currentCumulativeBalanceByLast6MonthAvg = currentCumulativeBalanceByPredictionTimeAvg;
        let currentCumulativeBalanceByLast12MonthAvg = currentCumulativeBalanceByPredictionTimeAvg;

        let futureDate = new Date(currentDate);
        let interestCounter = 0;
        let currentCumulativeBalanceByPredictionTimeAvgInitialValue = 0;
        let currentCumulativeBalanceByLast3MonthAvgInitialValue = 0;
        let currentCumulativeBalanceByLast6MonthAvgInitialValue = 0;
        let currentCumulativeBalanceByLast12MonthAvgInitialValue = 0;
        const monthsForCapitalization = interestCapitalization;
        const interestRateForCapitalization = interestRate / (12 / interestCapitalization);

        while (futureDate < predictionEndDate) {
          if (interestCounter === 0) {
            currentCumulativeBalanceByPredictionTimeAvgInitialValue = currentCumulativeBalanceByPredictionTimeAvg;
            currentCumulativeBalanceByLast3MonthAvgInitialValue = currentCumulativeBalanceByLast3MonthAvg;
            currentCumulativeBalanceByLast6MonthAvgInitialValue = currentCumulativeBalanceByLast6MonthAvg;
            currentCumulativeBalanceByLast12MonthAvgInitialValue = currentCumulativeBalanceByLast12MonthAvg;
          }
          futureDate = Common.Utils.addMonths(futureDate, 1);
          currentCumulativeBalanceByPredictionTimeAvg += avgMonthlyBalanceByPredictionTime;
          currentCumulativeBalanceByLast3MonthAvg += avgMonthlyBalanceByLast3Months;
          currentCumulativeBalanceByLast6MonthAvg += avgMonthlyBalanceByLast6Months;
          currentCumulativeBalanceByLast12MonthAvg += avgMonthlyBalanceByLast12Months;

          if (interestCounter + 1 === monthsForCapitalization) {
            currentCumulativeBalanceByPredictionTimeAvg +=
              currentCumulativeBalanceByPredictionTimeAvgInitialValue * (interestRateForCapitalization / 100);
            currentCumulativeBalanceByLast3MonthAvg +=
              currentCumulativeBalanceByLast3MonthAvgInitialValue * (interestRateForCapitalization / 100);
            currentCumulativeBalanceByLast6MonthAvg +=
              currentCumulativeBalanceByLast6MonthAvgInitialValue * (interestRateForCapitalization / 100);
            currentCumulativeBalanceByLast12MonthAvg +=
              currentCumulativeBalanceByLast12MonthAvgInitialValue * (interestRateForCapitalization / 100);
            interestCounter = -1;
          }
          predictions.push({
            year: futureDate.getFullYear(),
            month: futureDate.getMonth() + 1,
            balance: Number(avgMonthlyBalanceByPredictionTime.toFixed(2)),
            cumulativeBalanceByPredictionTimeAvg: Number(currentCumulativeBalanceByPredictionTimeAvg.toFixed(2)),
            cumulativeBalanceByLast3MonthAvg: Number(currentCumulativeBalanceByLast3MonthAvg.toFixed(2)),
            cumulativeBalanceByLast6MonthAvg: Number(currentCumulativeBalanceByLast6MonthAvg.toFixed(2)),
            cumulativeBalanceByLast12MonthAvg: Number(currentCumulativeBalanceByLast12MonthAvg.toFixed(2)),
            isPrediction: true,
          });
          interestCounter++;
        }

        let chartDataWithPredictions = [...chartDataWithCumulative, ...predictions];
        // f(x) = ax + b (a - slope, b - predictionStartingAmount)
        const dataLength = chartDataWithPredictions.length;
        // dataLength zamiast dataLength - 1, aby uwzględnić wartość początkową
        const slope = (predictionTargetBalance - predictionStartingAmount) / dataLength;

        chartDataWithPredictions = chartDataWithPredictions.map((item, index) => ({
          ...item,
          cumulativeBalance: item.cumulativeBalance + predictionStartingAmount,
          cumulativeBalanceByPredictionTimeAvg: item.cumulativeBalanceByPredictionTimeAvg + predictionStartingAmount,
          cumulativeBalanceByLast3MonthAvg: item.cumulativeBalanceByLast3MonthAvg + predictionStartingAmount,
          cumulativeBalanceByLast6MonthAvg: item.cumulativeBalanceByLast6MonthAvg + predictionStartingAmount,
          cumulativeBalanceByLast12MonthAvg: item.cumulativeBalanceByLast12MonthAvg + predictionStartingAmount,
          targetBalance: slope * (index + 1) + predictionStartingAmount,
          yearMonth: new Date(item.year, item.month - 1).getTime(),
        }));

        const lastNonPredictionYearMonth = chartDataWithCumulative[chartDataWithCumulative.length - 1]?.yearMonth;

        return (
          <div className='overflow-auto'>
            <ResponsiveContainer minWidth={600} width='100%' height={props.chartHeight + 200}>
              <ComposedChart
                accessibilityLayer={false}
                data={chartDataWithPredictions}
                margin={{
                  left: 10,
                  right: 25,
                  top: 5,
                  bottom: 5,
                }}
              >
                <Scatter key={0} name={'Wydatki'} line={{ strokeWidth: 0.7 }} fill='#0d6efd' cursor='pointer' />
                <CartesianGrid strokeDasharray='1' />
                <XAxis
                  dataKey={'yearMonth'}
                  name={'Miesiąc'}
                  tickFormatter={(date) => new Date(date).getMonth() + 1 + '.' + new Date(date).getFullYear()}
                />
                <YAxis width={'auto'} unit=' zł' />
                <Area
                  type='monotone'
                  dataKey='cumulativeBalance'
                  stroke={chartColors.cumulativeBalance}
                  isAnimationActive={false}
                  name='Bilans'
                  unit=' zł'
                  activeDot={{ r: 6 }}
                  fill={chartColors.cumulativeBalanceArea}
                />
                {showAveragePredictionTime && (
                  <Line
                    type='monotone'
                    dataKey='cumulativeBalanceByPredictionTimeAvg'
                    stroke={chartColors.cumulativeBalanceByPredictionTimeAvg}
                    dot={false}
                    animationDuration={isSimulating ? 500 : 1500}
                    name='Średnia dotychczasowa'
                    unit=' zł'
                    activeDot={{ r: 6 }}
                  />
                )}
                {showAverage3Months && (
                  <Line
                    type='monotone'
                    dataKey='cumulativeBalanceByLast3MonthAvg'
                    stroke={chartColors.cumulativeBalanceByLast3MonthAvg}
                    dot={false}
                    name='Średnia 3M'
                    unit=' zł'
                    activeDot={{ r: 6 }}
                  />
                )}
                {showAverage6Months && (
                  <Line
                    type='monotone'
                    dataKey='cumulativeBalanceByLast6MonthAvg'
                    stroke={chartColors.cumulativeBalanceByLast6MonthAvg}
                    dot={false}
                    animationDuration={isSimulating ? 500 : 1500}
                    name='Średnia 6M'
                    unit=' zł'
                    activeDot={{ r: 6 }}
                  />
                )}
                {showAverage12Months && (
                  <Line
                    type='monotone'
                    dataKey='cumulativeBalanceByLast12MonthAvg'
                    stroke={chartColors.cumulativeBalanceByLast12MonthAvg}
                    dot={false}
                    animationDuration={isSimulating ? 500 : 1500}
                    name='Średnia 12M'
                    unit=' zł'
                    activeDot={{ r: 6 }}
                  />
                )}
                {lastNonPredictionYearMonth && (
                  <ReferenceLine
                    x={lastNonPredictionYearMonth}
                    strokeWidth={0.7}
                    stroke={chartColors.predictionReferenceColor}
                    strokeDasharray='5 5'
                  />
                )}
                {predictionTargetBalance > 0 && (
                  <Line
                    type='linear'
                    dataKey='targetBalance'
                    stroke={chartColors.targetColor}
                    strokeDasharray='10 10'
                    dot={false}
                    activeDot={false}
                    animationDuration={isSimulating ? 500 : 1500}
                    name='Cel'
                    unit=' zł'
                  />
                )}
                <Legend content={<RenderLegend />} verticalAlign='top' />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const cumulativeBalance = payload.find((p) => p.dataKey === 'cumulativeBalance');
                      const cumulativeBalanceByPredictionTimeAvg = payload.find(
                        (p) => p.dataKey === 'cumulativeBalanceByPredictionTimeAvg'
                      );
                      const cumulativeBalanceByLast3MonthAvg = payload.find(
                        (p) => p.dataKey === 'cumulativeBalanceByLast3MonthAvg'
                      );
                      const cumulativeBalanceByLast6MonthAvg = payload.find(
                        (p) => p.dataKey === 'cumulativeBalanceByLast6MonthAvg'
                      );
                      const cumulativeBalanceByLast12MonthAvg = payload.find(
                        (p) => p.dataKey === 'cumulativeBalanceByLast12MonthAvg'
                      );
                      const targetBalance = payload.find((p) => p.dataKey === 'targetBalance');
                      const isPrediction = payload[0]?.payload?.isPrediction;
                      const date = new Date(label!);
                      const month = date.getMonth() + 1;
                      let monthLabel = month.toString();
                      if (month.toString().length <= 1) {
                        monthLabel = '0' + month;
                      }
                      const year = date.getFullYear();
                      const header = `${monthLabel}.${year}${isPrediction ? ' (prognoza)' : ''}`;

                      const predictionMaxBalanceValue = !isPrediction
                        ? Number(cumulativeBalance?.value ?? 0)
                        : Math.max(
                            Number(cumulativeBalanceByPredictionTimeAvg?.value ?? 0),
                            Number(cumulativeBalanceByLast3MonthAvg?.value ?? 0),
                            Number(cumulativeBalanceByLast6MonthAvg?.value ?? 0),
                            Number(cumulativeBalanceByLast12MonthAvg?.value ?? 0)
                          );
                      const targetBalanceValue = Number(targetBalance?.value ?? 0);

                      const relativeError = predictionMaxBalanceValue - targetBalanceValue;
                      const absoluteError = Math.round(10000 * (relativeError / targetBalanceValue)) / 100;

                      const targetElement = targetBalance && (
                        <Row>
                          <Col
                            xs='auto'
                            style={{
                              fontSize: '12px',
                              color: chartColors.targetColor,
                              marginBottom: '2px',
                            }}
                          >
                            <span style={{ fontWeight: '500' }}>Cel:</span>{' '}
                            {Common.Utils.getCurrencyString(Number(targetBalance.value))}
                          </Col>
                          {targetBalanceValue !== 0 &&
                            (absoluteError < 0 ? (
                              <Col
                                xs='auto'
                                style={{
                                  fontSize: '12px',
                                  color: 'red',
                                  marginBottom: '2px',
                                }}
                              >
                                <span style={{ fontWeight: '500' }}>
                                  {Common.Utils.getCurrencyString(Number(relativeError))} ({absoluteError}%)
                                </span>
                              </Col>
                            ) : (
                              <Col
                                xs='auto'
                                style={{
                                  fontSize: '12px',
                                  color: 'green',
                                  marginBottom: '2px',
                                }}
                              >
                                <span style={{ fontWeight: '500' }}>
                                  +{Common.Utils.getCurrencyString(Number(relativeError))} (+{absoluteError}%)
                                </span>
                              </Col>
                            ))}
                        </Row>
                      );

                      const cumulativeBalanceElement = cumulativeBalance && (
                        <div style={{ fontSize: '12px', color: chartColors.cumulativeBalance, marginBottom: '3px' }}>
                          <span style={{ fontWeight: '500' }}>Bilans:</span>{' '}
                          {Common.Utils.getCurrencyString(Number(cumulativeBalance.value))}
                        </div>
                      );

                      const cumulativeBalanceByPredictionTimeAvgElement = cumulativeBalanceByPredictionTimeAvg && (
                        <div
                          style={{
                            fontSize: '12px',
                            color: chartColors.cumulativeBalanceByPredictionTimeAvg,
                            marginBottom: '2px',
                          }}
                        >
                          <span style={{ fontWeight: '500' }}>Średnia dotychczasowa:</span>{' '}
                          {Common.Utils.getCurrencyString(Number(cumulativeBalanceByPredictionTimeAvg.value))}
                        </div>
                      );
                      const cumulativeBalanceByLast3MonthAvgElement = cumulativeBalanceByLast3MonthAvg && (
                        <div
                          style={{
                            fontSize: '12px',
                            color: chartColors.cumulativeBalanceByLast3MonthAvg,
                            marginBottom: '2px',
                          }}
                        >
                          <span style={{ fontWeight: '500' }}>Średnia z 3 miesięcy:</span>{' '}
                          {Common.Utils.getCurrencyString(Number(cumulativeBalanceByLast3MonthAvg.value))}
                        </div>
                      );
                      const cumulativeBalanceByLast6MonthAvgElement = cumulativeBalanceByLast6MonthAvg && (
                        <div
                          style={{
                            fontSize: '12px',
                            color: chartColors.cumulativeBalanceByLast6MonthAvg,
                            marginBottom: '2px',
                          }}
                        >
                          <span style={{ fontWeight: '500' }}>Średnia z 6 miesięcy:</span>{' '}
                          {Common.Utils.getCurrencyString(Number(cumulativeBalanceByLast6MonthAvg.value))}
                        </div>
                      );
                      const cumulativeBalanceByLast12MonthAvgElement = cumulativeBalanceByLast12MonthAvg && (
                        <div style={{ fontSize: '12px', color: chartColors.cumulativeBalanceByLast12MonthAvg }}>
                          <span style={{ fontWeight: '500' }}>Średnia z 12 miesięcy:</span>{' '}
                          {Common.Utils.getCurrencyString(Number(cumulativeBalanceByLast12MonthAvg.value))}
                        </div>
                      );

                      return (
                        <div
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '13px',
                              fontWeight: 'bold',
                              color: '#333',
                              marginBottom: '6px',
                              borderBottom: '1px solid #e0e0e0',
                              paddingBottom: '4px',
                            }}
                          >
                            {header}
                          </div>
                          {targetElement}
                          {!isPrediction && <div>{cumulativeBalanceElement}</div>}
                          {isPrediction && (
                            <div>
                              {cumulativeBalanceByPredictionTimeAvgElement}
                              {cumulativeBalanceByLast3MonthAvgElement}
                              {cumulativeBalanceByLast6MonthAvgElement}
                              {cumulativeBalanceByLast12MonthAvgElement}
                            </div>
                          )}
                        </div>
                      );
                    }

                    return null;
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        );
      };
      let cardBody: React.JSX.Element;
      if (financialData === undefined) {
        cardBody = <div style={{ height: 200 }}>{Common.Ui.showLoadingSpinnerAbsolute(true)}</div>;
      } else {
        if (financialData.length === 0) {
          return <></>;
        } else {
          cardBody = generateChartData(financialData);
        }
      }

      return (
        <Card className='mt-3 mb-2'>
          <Card.Header className='statistics-card-header'>
            <div>Przewidywania</div>
          </Card.Header>
          <Card.Body className='pb-0 pt-2'>{cardBody}</Card.Body>
        </Card>
      );
    },
    [
      props.chartHeight,
      predictionStartDate,
      predictionEndDate,
      dataCalculationDate,
      predictionTargetBalance,
      predictionStartingAmount,
      interestRate,
      interestCapitalization,
      showAveragePredictionTime,
      showAverage3Months,
      showAverage6Months,
      showAverage12Months,
      debouncedInterestRateChanged,
      debouncedPredictionTargetBalance,
      debouncedPredictionStartingAmount,
      isSimulating,
      handleSimulation,
    ]
  );

  return props.showPredictions && generatePredictionsData(props.monthlyFinancialData);
};

export default PredictionStatistics;
